import { DownloadIcon, LinkIcon } from '@chakra-ui/icons';
import {
    Avatar,
    Badge,
    Box,
    Button,
    HStack,
    SimpleGrid,
    Spinner,
    Text,
    useToast,
    VStack
} from '@chakra-ui/react';
import { format } from 'date-fns';
import React from 'react';
import ErrorState from '../../components/states/ErrorState';
import PageLayout from '../../components/page-layout';
import PageCard from '../../components/page-card';
import { translate, TranslationKeys } from '../../i18n';
import { EventDto, EventStatus, EventType } from '../../models/event/dto/event.dto';
import { paramFromWindowLocation } from '../../utils/url-parser';
import { useCancelEvent } from '../event-management/hooks/useCancelEvent';
import { useGetEvent } from '../event-management/hooks/useGetEvent';
import EventParticipantsCard from './components/EventParticipantsCard';
import EventWaitlistCard from './components/EventWaitlistCard';
import { useIsOrgManager } from '../auth/roles';
import { buildEventParticipantsCsv, downloadCsv, hasParticipantsOrTeamsToExport } from './exportEventParticipants';

export default function EventDetailsPage() {
    const eventId = Number.parseInt(paramFromWindowLocation('event'));
    const { data: event, isLoading, error, refetch } = useGetEvent(eventId);
    const cancelEventMutation = useCancelEvent();
    // Waitlist management, kicks and event cancellation are admin-only on the
    // API; coaches get a read-only event view from their calendar.
    const isOrgManager = useIsOrgManager();
    const toast = useToast();

    if (isLoading && !event) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <Spinner size="lg" />
            </Box>
        );
    }

    if (error || !event) {
        return (
            <ErrorState message={translate('event.error_loading')} onRetry={refetch} />
        );
    }

    const handleShareEvent = async () => {
        const eventLink = event.sharableLink;
        if (!eventLink) {
            toast({
                title: translate('event.link_not_available'),
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
            return;
        }

        try {
            await navigator.clipboard.writeText(eventLink);
            toast({
                title: translate('event.link_copied'),
                status: 'success',
                duration: 2000,
                isClosable: true,
            });
        } catch (error) {
            toast({
                title: translate('event.copy_failed'),
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        }
    };

    const handleExportParticipants = () => {
        if (!hasParticipantsOrTeamsToExport(event)) {
            toast({
                title: translate('event.export_empty'),
                status: 'info',
                duration: 3000,
                isClosable: true,
            });
            return;
        }
        const csv = buildEventParticipantsCsv(event, (key: string) => String(translate(key as TranslationKeys) ?? key));
        const baseName = event.name.replace(/[^\w\s-]/g, '').replace(/\s+/g, '_') || 'event';
        const filename = `${baseName}_${event.teamBased ? 'teams' : 'participants'}`;
        downloadCsv(csv, filename);
        toast({
            title: translate('event.export_success'),
            status: 'success',
            duration: 2000,
            isClosable: true,
        });
    };

    return (
        <PageLayout>
            <PageCard
                showBackButton
                title={translate('event.details') || 'Event Details'}
                rightHeader={
                    <Button
                        leftIcon={<LinkIcon />}
                        size="sm"
                        colorScheme="brand"
                        variant="outline"
                        onClick={handleShareEvent}
                    >
                        {translate('event.copy_link')}
                    </Button>
                }>
                <EventInformation
                    event={event}
                    cancelEventMutation={isOrgManager ? cancelEventMutation : undefined}
                />
            </PageCard>

            <PageCard
                title={event.teamBased
                    ? `${translate('event.teams') || 'Teams'} (${(event.teams?.length || 0)}/${Math.floor(event.capacity / (event.teamSize || 1))})`
                    : `${translate('event.participants') || 'Participants'} (${(event.participants?.length || 0)}/${event.capacity})`}
                rightHeader={
                    <Button
                        leftIcon={<DownloadIcon />}
                        size="sm"
                        colorScheme="green"
                        variant="outline"
                        onClick={handleExportParticipants}
                        isDisabled={!hasParticipantsOrTeamsToExport(event)}
                    >
                        {translate('event.export_participants') || 'Export to CSV'}
                    </Button>
                }>
                <EventParticipantsCard event={event} />
            </PageCard>

            {isOrgManager && (
                <PageCard
                    title={translate('event.waitlist.card_title')}>
                    <EventWaitlistCard event={event} />
                </PageCard>
            )}
        </PageLayout>
    );
}

interface EventInformationProps {
    event: EventDto;
    cancelEventMutation?: ReturnType<typeof useCancelEvent>;
}

export const formatDate = (date: Date | string) => {
    return format(new Date(date), 'dd MMM yyyy - HH:mm');
};

const EventInformation: React.FC<EventInformationProps> = ({ event, cancelEventMutation }) => {

    const getEventTypeBadge = (type: EventType) => {
        const colorMap = {
            [EventType.COMPETITION]: 'purple',
            [EventType.CLASS]: 'blue',
            [EventType.OFFICIAL_COMPETITION]: 'orange',
            [EventType.OTHER]: 'gray',
        };
        const textMap = {
            [EventType.COMPETITION]: translate('event.type.competition') || 'Competition',
            [EventType.CLASS]: translate('event.type.class') || 'Class',
            [EventType.OFFICIAL_COMPETITION]: translate('event.type.official_competition') || 'Official Tournament',
            [EventType.OTHER]: translate('event.type.other') || 'Other',
        };
        return <Badge colorScheme={colorMap[type]}>{textMap[type]}</Badge>;
    };

    const getEventStatusBadge = (status: EventStatus) => {
        let color = 'gray';
        let text = status;

        switch (status) {
            case EventStatus.ACTIVE:
                color = 'green';
                text = translate('event.status.active') || 'Active';
                break;
            case EventStatus.COMPLETED:
                color = 'blue';
                text = translate('event.status.completed') || 'Completed';
                break;
            case EventStatus.CANCELLED:
                color = 'red';
                text = translate('event.status.cancelled') || 'Cancelled';
                break;
        }

        return <Badge colorScheme={color}>{text}</Badge>;
    };

    const formatLevelRange = () => {
        if (event.minLevel && event.maxLevel) {
            return `${event.minLevel} - ${event.maxLevel}`;
        } else if (event.minLevel) {
            return `${event.minLevel}+`;
        } else if (event.maxLevel) {
            return `<= ${event.maxLevel}`;
        }
        return '-';
    };

    const formatRankingRange = () => {
        if (event.minRanking && event.maxRanking) {
            return `${event.minRanking} - ${event.maxRanking}`;
        } else if (event.minRanking) {
            return `${event.minRanking}+`;
        } else if (event.maxRanking) {
            return `<= ${event.maxRanking}`;
        }
        return '-';
    };

    return (
        <VStack spacing={4} align="stretch" p={4}>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} padding={4}>
                <VStack align="start" spacing={3}>
                    <InfoRow
                        label={translate('event.name') || 'Name'}
                        value={event.name}
                    />
                    <HStack justify="space-between" width="100%">
                        <InfoRow
                            label={translate('event.start_time') || 'Start Time'}
                            value={formatDate(event.startTime)}
                        />
                        {cancelEventMutation && event.status !== EventStatus.CANCELLED && (
                            <Button
                                size="sm"
                                colorScheme="red"
                                variant="outline"
                                onClick={() => cancelEventMutation.mutate(event.id)}
                                isLoading={cancelEventMutation.isLoading}
                                loadingText={translate('event.canceling') || 'Canceling...'}
                            >
                                {translate('event.cancel_event') || 'Cancel Event'}
                            </Button>
                        )}
                    </HStack>
                    <InfoRow
                        label={translate('event.duration') || 'Duration'}
                        value={`${Math.round(event.duration / 60000)} ${translate('event.minutes') || 'minutes'}`}
                    />
                    <InfoRow
                        label={translate('event.location') || 'Location'}
                        value={event.location}
                    />
                    <InfoRow
                        label={translate('event.courts') || 'Courts'}
                        value={event.courts.length > 0
                            ? event.courts.map(c => c.name).join(', ')
                            : '-'}
                    />
                    {/* Coach/animator — optional, an event may have none */}
                    <HStack>
                        <Text fontWeight="bold" minWidth="120px">
                            {translate('event.coach')}:
                        </Text>
                        {event.coach ? (
                            <HStack spacing={2}>
                                <Avatar
                                    size="xs"
                                    name={`${event.coach.firstName} ${event.coach.lastName}`}
                                    src={event.coach.avatar}
                                />
                                <Text>{`${event.coach.firstName} ${event.coach.lastName}`}</Text>
                            </HStack>
                        ) : (
                            <Text>-</Text>
                        )}
                    </HStack>
                </VStack>

                <VStack align="start" spacing={3}>
                    <HStack>
                        <Text fontWeight="bold">{translate('event.type_label') || 'Type'}:</Text>
                        {getEventTypeBadge(event.eventType)}
                    </HStack>
                    <HStack>
                        <Text fontWeight="bold">{translate('event.status_label') || 'Status'}:</Text>
                        {getEventStatusBadge(event.status)}
                    </HStack>
                    <InfoRow
                        label={translate('event.capacity') || 'Capacity'}
                        value={event.teamBased
                            ? `${(event.teams?.length || 0)} / ${Math.floor(event.capacity / (event.teamSize || 1))} ${translate('event.teams') || 'teams'}`
                            : `${(event.participants?.length || 0)} / ${event.capacity}`}
                    />
                    {event.teamBased && event.teamSize && (
                        <InfoRow
                            label={translate('event.team_size') || 'Team Size'}
                            value={`${event.teamSize} ${translate('event.players_per_team') || 'players per team'}`}
                        />
                    )}
                    <InfoRow
                        label={translate('event.level_range') || 'Level Range'}
                        value={formatLevelRange()}
                    />
                    {event.eventType === EventType.OFFICIAL_COMPETITION && (
                        <InfoRow
                            label={translate('event.ranking_range') || 'Ranking Range'}
                            value={formatRankingRange()}
                        />
                    )}
                    {event.price !== undefined && event.price !== null && (
                        <InfoRow
                            label={translate('event.price') || 'Price'}
                            value={`${event.price}`}
                        />
                    )}
                    {event.prizes && event.prizes.length > 0 && (
                        <VStack align="start" spacing={2}>
                            <Text fontWeight="bold">{translate('event.prizes') || 'Prizes'}:</Text>
                            {event.prizes.map((prize, index) => (
                                <Badge key={index} colorScheme="yellow">
                                    #{prize.place}: {prize.reward}
                                </Badge>
                            ))}
                        </VStack>
                    )}
                </VStack>
            </SimpleGrid>
        </VStack>
    );
};

interface InfoRowProps {
    label: string;
    value: string;
}

const InfoRow: React.FC<InfoRowProps> = ({ label, value }) => (
    <HStack>
        <Text fontWeight="bold" minWidth="120px">{label}:</Text>
        <Text>{value}</Text>
    </HStack>
);
